import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Activity, Calendar, GripVertical, X } from "lucide-react";
import RawTemperatureTimelineChart from './raw-temperature-timeline-chart';
import { apiClient } from '@/api/client';
import { ApiPath } from '@/api/path';
import { TSMetric } from '@/query/query';
import { TEN_SECONDS } from '@/dateRanges/windowSizes';

// InfluxDB temperature data point
interface InfluxTempData {
  time: number; // timestamp in milliseconds
  value: number; // temperature value
  label: string; // sensor name
}

// Athena anomaly marker
interface AthenaAnomaly {
  time: string; // ISO timestamp
  temp_c: number;
  temp_status: string; // 'too_high', 'too_low', 'normal'
  predicted_min_c: number;
  predicted_max_c: number;
}

interface DraggableRawTempCardProps {
  installationId: string;
  organizationId: string;
  locationName: string;
  sensorKitSerialNumber: string;
  maxDurationMinutes?: number;
  onRemove?: () => void;
  className?: string;
}

const DraggableRawTempCard: React.FC<DraggableRawTempCardProps> = ({ 
  installationId,
  organizationId,
  locationName,
  sensorKitSerialNumber,
  maxDurationMinutes = 2,
  onRemove,
  className = ""
}) => {
  const [influxData, setInfluxData] = useState<InfluxTempData[]>([]);
  const [athenaAnomalies, setAthenaAnomalies] = useState<AthenaAnomaly[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>('1d');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Calculate date range
      const now = new Date();
      const daysBack = parseInt(timeRange.replace('d', ''));
      const from = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);

      // 1. Fetch raw temperature data from InfluxDB
      const influxResponse = await apiClient.post(ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY, {
        metrics: [TSMetric.ALL_TEMPERATURES],
        window_size: TEN_SECONDS.value,
        start: from.toISOString(),
        stop: now.toISOString(),
        organization_id: parseInt(organizationId),
        installation_id: [parseInt(installationId)],
        target_unit: "degC"
      });

      // Process InfluxDB response
      const influxResults: InfluxTempData[] = [];
      if (influxResponse.data && influxResponse.data.data) {
        influxResponse.data.data.forEach((series: any) => {
          series.data.forEach((point: [number, number]) => {
            influxResults.push({
              time: point[0],
              value: point[1],
              label: series.name
            });
          });
        });
      }
      setInfluxData(influxResults);

      // 2. Fetch anomaly markers
      const anomalyResponse = await apiClient.post('on-periods/temp-anomalies/', {
        installation_id: parseInt(installationId),
        start: from.toISOString(),
        end: now.toISOString(),
        sensor_name: 'Compressor outlet',
        min_duration_minutes: maxDurationMinutes,
      });

      if (anomalyResponse.data.error) {
        throw new Error(anomalyResponse.data.error);
      }

      setAthenaAnomalies(anomalyResponse.data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [installationId, organizationId, locationName, sensorKitSerialNumber, maxDurationMinutes, timeRange]);

  const anomalyCount = athenaAnomalies.length;

  return (
    <Card className={`relative ${className}`}>
      {/* Drag Handle */}
      <div className="absolute top-2 left-2 cursor-move opacity-50 hover:opacity-100 transition-opacity">
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>

      {/* Remove Button */}
      {onRemove && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-6 w-6 p-0"
          onClick={onRemove}
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="ml-6">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="h-5 w-5" />
              Raw Temperature Timeline
            </CardTitle>
            <CardDescription className="mt-1">
              {locationName} • Serial: {sensorKitSerialNumber} • Individual measurements
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Time Range Selector */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1d">1 Day</SelectItem>
                <SelectItem value="3d">3 Days</SelectItem>
                <SelectItem value="7d">7 Days</SelectItem>
                <SelectItem value="14d">14 Days</SelectItem>
                <SelectItem value="30d">30 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {anomalyCount > 0 && (
            <div className="ml-auto">
              <div className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                ⭐ {anomalyCount} anomalies
              </div>
            </div>
          )}
        </div>

        {error ? (
          <div className="text-center py-8">
            <div className="text-red-500 mb-2">Error loading data</div>
            <div className="text-sm text-muted-foreground">{error}</div>
            <Button variant="outline" onClick={fetchData} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : loading ? (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <div className="text-muted-foreground">Loading raw temperature data...</div>
          </div>
        ) : influxData.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <div className="text-muted-foreground">No raw temperature data found</div>
            <div className="text-sm text-muted-foreground mt-2">
              No measurements detected for installation {installationId} in the past {timeRange}
            </div>
          </div>
        ) : (
          <RawTemperatureTimelineChart
            influxData={influxData}
            athenaAnomalies={athenaAnomalies}
            timeRange={timeRange}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default DraggableRawTempCard;

